import React from 'react';
import Icon from '../../../components/AppIcon';

const AIAnalysisPanel = ({ analysis, loading, onAnalyze }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-8">
        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-neutral-600">Analyzing email delivery failures with AI...</p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-8">
        <div className="text-center">
          <Icon name="Sparkles" size={48} className="mx-auto text-blue-500 mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">AI-Powered Delivery Analysis</h3>
          <p className="text-neutral-600 mb-6">Use OpenAI to analyze delivery failures and get actionable insights</p>
          <button
            onClick={onAnalyze}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <Icon name="Sparkles" size={18} />
            Analyze with AI
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-start gap-3">
          <Icon name="Sparkles" size={24} className="text-blue-600 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">AI Analysis Summary</h3>
            <p className="text-neutral-700">{analysis?.summary}</p>
            <div className="mt-3 flex items-center gap-4 text-sm text-neutral-600">
              <span>Analyzed {analysis?.emailCount} failed emails</span>
              <span>•</span>
              <span>{new Date(analysis?.analyzedAt)?.toLocaleString()}</span>
            </div>
          </div>
          <button
            onClick={onAnalyze}
            className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 border border-blue-200"
          >
            <Icon name="RefreshCw" size={16} />
            Re-analyze
          </button>
        </div>
      </div>
      {/* Failure Patterns */}
      {analysis?.patterns && analysis?.patterns?.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={20} />
            Failure Patterns Detected
          </h3>
          <div className="space-y-4">
            {analysis?.patterns?.map((pattern, index) => (
              <div key={index} className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-neutral-900">{pattern?.patternName}</h4>
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                    {pattern?.affectedEmails || pattern?.frequency} emails
                  </span>
                </div>
                <p className="text-sm text-neutral-700">{pattern?.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Root Causes */}
      {analysis?.rootCauses && analysis?.rootCauses?.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Icon name="AlertCircle" size={20} />
            Root Causes
          </h3>
          <div className="space-y-3">
            {analysis?.rootCauses?.map((cause, index) => (
              <div key={index} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={18} className="text-orange-600 mt-1" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900 mb-1">{cause?.cause}</h4>
                    <p className="text-sm text-neutral-600 mb-2">Related to: {cause?.relatedPattern}</p>
                    {cause?.technicalDetails && (
                      <p className="text-sm text-neutral-700 bg-white p-2 rounded border border-orange-100">
                        {cause?.technicalDetails}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Recommendations */}
      {analysis?.recommendations && analysis?.recommendations?.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Icon name="Lightbulb" size={20} />
            AI-Generated Recommendations
          </h3>
          <div className="space-y-4">
            {analysis?.recommendations?.map((rec, index) => {
              const priorityColors = {
                high: 'bg-red-100 text-red-700 border-red-200',
                medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                low: 'bg-green-100 text-green-700 border-green-200'
              };
              const priorityColor = priorityColors?.[rec?.priority?.toLowerCase()] || priorityColors?.medium;

              return (
                <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-neutral-900 flex-1">{rec?.action}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${priorityColor}`}>
                      {rec?.priority} priority
                    </span>
                  </div>
                  <p className="text-sm text-neutral-700 mb-3">
                    <strong>Expected Impact:</strong> {rec?.expectedImpact}
                  </p>
                  {rec?.implementationSteps && rec?.implementationSteps?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-neutral-900 mb-2">Implementation Steps:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        {rec?.implementationSteps?.map((step, stepIndex) => (
                          <li key={stepIndex} className="text-sm text-neutral-700">{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnalysisPanel;
